import { useTranslation } from 'react-i18next';

import { useAcceptAllFrameQuery, useFrameCountQuery } from '@/hook';
import {
  VButton,
  VFlex,
  VInput,
  VSelect,
  VStack,
  VTabContent,
  VTabTrigger,
  VTabTriggerContainer,
  Verified,
  useVModal,
} from '@/package';
import type { Task, User } from '@/type';

import { ReviewerHandoff } from '../Task';

export const TaskDetailTabOperation = ({
  task,
  keyword,
  onKeywordChange,
  selectedMember,
  setSelectedMember,
  allMemberValue,
}: {
  task: Task;
  keyword: string;
  onKeywordChange: (value: string) => void;
  selectedMember: User['id'];
  setSelectedMember: (value: User['id']) => void;
  allMemberValue: string;
}) => {
  const { t } = useTranslation();

  return (
    <VFlex
      justify="between"
      className="w-full px-8 py-2 shadow-sm"
      items="center"
    >
      <VFlex gap={1} items="center">
        <VTabTriggerContainer>
          <VTabTrigger value="0"> List </VTabTrigger>
          <VTabTrigger value="1"> Statistics </VTabTrigger>
          <VTabTrigger value="2"> Overview </VTabTrigger>
        </VTabTriggerContainer>
        <VTabContent value="0">
          <div className="w-[264px]">
            <VInput
              size={'md'}
              type="search"
              fluid
              placeholder={t('frame.frameSeqSearchPlaceholder.crenic')}
              value={keyword}
              onChange={e => onKeywordChange(e.target.value)}
            />
          </div>
        </VTabContent>
      </VFlex>

      <VTabContent value="0">
        <VFlex gap={0.5} items="center">
          <ReviewerHandoff.AllButton task={task} />
          <AllVerifyButton task={task} />
        </VFlex>
      </VTabContent>

      <VTabContent value="1">
        <StatisticsMemberSelect
          task={task}
          selectedMember={selectedMember}
          setSelectedMember={setSelectedMember}
          allMemberValue={allMemberValue}
        />
      </VTabContent>
    </VFlex>
  );
};

const AllVerifyButton = ({ task }: { task: Task }) => {
  const { data: acceptableCount } = useFrameCountQuery({
    taskID: task.id,
    workState: ['Reviewing', 'Review'],
  });
  const isPreAnnotationRunning =
    task.operation?.find(op => op.operationType === 'AIAnnotation')
      ?.operationState === 'Running';
  const isAllVerfiable = !!acceptableCount && !isPreAnnotationRunning;
  const { mutate: acceptAllFrame, isPending: isAcceptAllFramePending } =
    useAcceptAllFrameQuery();
  const { openConfirm } = useVModal();
  const onAllAcceptClick = () => {
    openConfirm({
      title: 'All verify',
      message: () => (
        <VStack gap={0.25}>
          <div className="text-body-xl text-black">
            Verify all ‘In review’ frames?
          </div>
          <div className="text-body-lg text-gray-600">
            Once approved, this action cannot be undone.
            <br />
            The frame status will be changed to ‘Completed.’
          </div>
        </VStack>
      ),
      onConfirm: () => allSubmit(),
      confirmText: 'All verify',
    });
  };

  const allSubmit = () => {
    acceptAllFrame({ pTaskID: task.id });
  };

  return (
    <VButton
      disabled={!isAllVerfiable}
      loading={isAcceptAllFramePending}
      size="md"
      type="button"
      onClick={onAllAcceptClick}
    >
      <Verified />
      All verfiy ({acceptableCount ?? 0})
    </VButton>
  );
};

const StatisticsMemberSelect = ({
  task,
  selectedMember,
  setSelectedMember,
  allMemberValue,
}: {
  task: Task;
  selectedMember: User['id'];
  setSelectedMember: (value: User['id']) => void;
  allMemberValue: string;
}) => {
  const allItem = {
    label: 'All members',
    value: allMemberValue,
  };
  const itemList =
    task.userTask?.map(user => ({
      label: user.user?.name || user.user?.email.split('@')[0],
      value: user.user?.id,
    })) ?? [];

  return (
    <div className="w-[224px]">
      <VSelect
        itemList={[allItem, ...itemList]}
        value={selectedMember}
        onChange={e => setSelectedMember(e.target.value)}
        fluid
      />
    </div>
  );
};
